import { ArrowRight, Layers, MapPin } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Job } from '../JobCard';

import { tokenize } from './ranking';

export interface SimilarJobsProps {
  currentJob: Job;
  allJobs:    Job[];
  onSelect:   (job: Job) => void;
}

function jaccardScore(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const token of a) if (b.has(token)) intersection++;
  const union = a.size + b.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

function skillTokens(job: Job): Set<string> {
  const text =
    (job.must_have_skills ?? []).join(' ') +
    ' ' +
    (job.skills ?? []).join(' ');
  return tokenize(text);
}

const SimilarJobs: React.FC<SimilarJobsProps> = ({ currentJob, allJobs, onSelect }) => {
  const { t } = useTranslation();
  const currentTokens = useMemo(() => skillTokens(currentJob), [currentJob]);

  const similar = useMemo(() => {
    if (currentTokens.size === 0) return [];
    return allJobs
      .filter(j => j.id !== currentJob.id)
      .map(j => ({ job: j, score: jaccardScore(currentTokens, skillTokens(j)) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);
  }, [currentJob.id, currentTokens, allJobs]);

  if (similar.length === 0) return null;

  return (
    <div className="ds-card-flush">
      <div className="flex items-center gap-2 px-4 py-3 border-b"
        style={{ borderColor: 'var(--ds-divider)', background: 'var(--ds-bg-recessed)' }}>
        <Layers className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--ds-brand)' }} />
        <span className="t-label">
          {t('dashboard.jobs.jobDrawer.similarJobs', 'Similar Jobs')}
        </span>
        <span className="ds-chip ds-chip-brand t-num ml-auto">
          {similar.length}
        </span>
      </div>

      <div style={{ background: 'var(--ds-bg-card)' }}>
        {similar.map(({ job, score }, idx) => {
          const overlap = Math.round(score * 100);
          const scoreColor =
            overlap >= 60 ? 'var(--ds-ok)' :
            overlap >= 40 ? 'var(--ds-info)' :
            overlap >= 20 ? 'var(--ds-warn)' : 'var(--ds-text-muted)';

          const initial = (job.company?.trim()[0] ?? '?').toUpperCase();

          return (
            <button
              key={job.id}
              type="button"
              onClick={() => onSelect(job)}
              className="w-full text-left group transition-colors"
              style={{
                borderTop: idx > 0 ? '1px solid var(--ds-divider)' : 'none',
              }}
            >
              <div className="flex items-center gap-3 px-4 py-3
                              group-hover:bg-[var(--ds-bg-hover)] transition-colors">
                <div className="ds-icon-square ds-icon-square-sm t-tag t-strong rounded-full shrink-0">
                  {initial}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="t-card-title truncate">
                    {job.title}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="t-meta truncate">
                      {job.company}
                    </span>
                    {job.location && (
                      <>
                        <span className="t-meta">·</span>
                        <span className="t-tag t-muted flex items-center gap-0.5">
                          <MapPin className="w-2.5 h-2.5 shrink-0 t-danger" />
                          {job.location}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span className="ds-chip t-num"
                    style={{ color: scoreColor }}>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: scoreColor }} />
                    {overlap}%
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: 'var(--ds-brand)' }} />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(SimilarJobs);
